import React, { useState, useEffect, useCallback } from "react";
import api from "services/api";

const Productions = () => {
  const [list, setList] = useState([]);
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [loading, setLoading] = useState(false);

  // Draft state for inline adding new rows
  const [draft, setDraft] = useState({
    production_date: date,
    unit: "",
    line_no: "",
    buyer: "",
    style: "",
    target: 0,
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/planing/productions?date=${date}`);
      setList(res.data);
    } catch (err) {
      console.error("Error fetching data", err);
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => {
    fetchData();
    setDraft((prev) => ({ ...prev, production_date: date }));
  }, [fetchData, date]);

  // Handle Inline Create
  const handleCreate = async () => {
    try {
      await api.post("/api/productions", draft);
      setDraft({
        ...draft,
        unit: "",
        line_no: "",
        buyer: "",
        style: "",
        target: 0,
      });
      fetchData();
    } catch (err) {
      alert(err.response?.data?.message || "Check for duplicate entry");
    }
  };

  // Handle Inline Update (Hourly or Text fields)
  const handleUpdate = async (id, field, value) => {
    try {
      const res = await api.put(`/api/productions/${id}`, { [field]: value });
      // Update the specific row in local state to reflect new Appends (total_prod, variance)
      setList((prev) =>
        prev.map((item) => (item.id === id ? res.data.data : item))
      );
    } catch (err) {
      console.error("Update failed", err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this line?")) return;
    await api.delete(`/api/productions/${id}`);
    fetchData();
  };

  return (
    <div className="p-2 bg-gray-100 min-h-screen font-sans">
      {/* Header / Filter Section */}
      <div className="flex justify-between items-center bg-[#B4E4FF] p-3 border border-slate-400 mb-2">
        <h2 className="font-bold text-gray-800">
          DAILY HOURLY PRODUCTION REPORT
        </h2>
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold">DATE:</span>
          <input
            type="date"
            className="border p-1 text-xs"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </div>
      </div>

      <div className="overflow-x-auto shadow-lg bg-white">
        <table className="w-full text-[10px] border-collapse border border-slate-400">
          <thead className="bg-[#B4E4FF] sticky top-0 uppercase">
            <tr>
              <th className="border border-slate-400 p-1">Unit</th>
              <th className="border border-slate-400 p-1">Line</th>
              <th className="border border-slate-400 p-1">Buyer</th>
              <th className="border border-slate-400 p-1">Style</th>
              <th className="border border-slate-400 p-1">Target</th>
              {[...Array(18)].map((_, i) => (
                <th key={i} className="border border-slate-400 p-1 w-8">
                  {i + 1}H
                </th>
              ))}
              <th className="border border-slate-400 p-1 bg-green-100 font-bold">
                Total
              </th>
              <th className="border border-slate-400 p-1">+/-</th>
              <th className="border border-slate-400 p-1">Action</th>
            </tr>
          </thead>
          <tbody>
            {/* INLINE ADD ROW */}
            <tr className="bg-blue-50">
              <td className="border border-slate-300 p-0">
                <input
                  placeholder="Unit"
                  className="w-full p-1 bg-transparent"
                  value={draft.unit}
                  onChange={(e) => setDraft({ ...draft, unit: e.target.value })}
                />
              </td>
              <td className="border border-slate-300 p-0">
                <input
                  placeholder="Line"
                  className="w-full p-1 bg-transparent"
                  value={draft.line_no}
                  onChange={(e) =>
                    setDraft({ ...draft, line_no: e.target.value })
                  }
                />
              </td>
              <td className="border border-slate-300 p-0">
                <input
                  placeholder="Buyer"
                  className="w-full p-1 bg-transparent"
                  value={draft.buyer}
                  onChange={(e) =>
                    setDraft({ ...draft, buyer: e.target.value })
                  }
                />
              </td>
              <td className="border border-slate-300 p-0">
                <input
                  placeholder="Style"
                  className="w-full p-1 bg-transparent"
                  value={draft.style}
                  onChange={(e) =>
                    setDraft({ ...draft, style: e.target.value })
                  }
                />
              </td>
              <td className="border border-slate-300 p-0">
                <input
                  placeholder="Target"
                  type="number"
                  className="w-full p-1 bg-transparent text-center"
                  value={draft.target}
                  onChange={(e) =>
                    setDraft({ ...draft, target: e.target.value })
                  }
                />
              </td>
              <td
                colSpan={18}
                className="border border-slate-300 p-1 text-center text-gray-400 italic"
              >
                Fill details to add new line
              </td>
              <td
                colSpan={3}
                className="border border-slate-300 p-1 text-center"
              >
                <button
                  onClick={handleCreate}
                  className="bg-green-600 text-white px-2 py-1 rounded text-[9px] hover:bg-green-700"
                >
                  ADD LINE
                </button>
              </td>
            </tr>

            {/* DATA LIST ROWS */}
            {list.map((row) => (
              <tr key={row.id} className="hover:bg-yellow-50 text-center">
                <td className="border border-slate-300 font-bold">
                  {row.unit}
                </td>
                <td className="border border-slate-300 font-bold">
                  {row.line_no}
                </td>
                <td className="border border-slate-300 text-left px-1">
                  {row.buyer}
                </td>
                <td className="border border-slate-300 text-left px-1">
                  {row.style}
                </td>
                <td className="border border-slate-300 font-bold">
                  {row.target}
                </td>

                {/* Hourly Production Inputs (h1-h18) */}
                {[...Array(18)].map((_, i) => (
                  <td key={i} className="border border-slate-300 p-0">
                    <input
                      type="number"
                      className="w-full text-center p-1 outline-none focus:bg-yellow-200 transition-colors"
                      defaultValue={row[`h${i + 1}`] || ""}
                      onBlur={(e) =>
                        handleUpdate(row.id, `h${i + 1}`, e.target.value)
                      }
                    />
                  </td>
                ))}

                <td className="border border-slate-300 bg-green-50 font-bold">
                  {row.total_prod}
                </td>
                <td
                  className={`border border-slate-300 font-bold ${
                    row.variance < 0 ? "text-red-500" : "text-green-600"
                  }`}
                >
                  {row.variance}
                </td>
                <td className="border border-slate-300 p-1">
                  <button
                    onClick={() => handleDelete(row.id)}
                    className="text-red-500 hover:text-red-700"
                  >
                    ✖
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Productions;
